import React, { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, Search } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import { BreakGlassIncidentEntry } from '../../types';
import { getBreakGlassIncidents, getCurrentUserPermissions } from '../../utils/auth/authService';

const cardStyle = (border: string, background: string): React.CSSProperties => ({
  background,
  border: `1px solid ${border}`,
  borderRadius: 12,
});

const identityLabel = (name?: string, username?: string, email?: string, userId?: string) => {
  const primary = name || username || email || userId || 'Unknown';
  const secondary = email && email !== primary ? email : username && username !== primary ? username : '';
  return { primary, secondary };
};

const BreakGlassIncidentsPage: React.FC = () => {
  const { theme } = useTheme();
  const [authorized, setAuthorized] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [incidents, setIncidents] = useState<BreakGlassIncidentEntry[]>([]);
  const [selectedIncident, setSelectedIncident] = useState<BreakGlassIncidentEntry | null>(null);
  const [filters, setFilters] = useState({ q: '', pipelineId: '' });

  const loadIncidents = async (nextFilters = filters) => {
    setLoading(true);
    try {
      const data = await getBreakGlassIncidents({
        limit: 200,
        q: nextFilters.q || undefined,
        pipelineId: nextFilters.pipelineId || undefined,
      });
      setIncidents(data);
      setSelectedIncident((current) => data.find((item) => item.version_id === current?.version_id) || data[0] || null);
    } catch (error) {
      console.error('Failed to load break-glass incidents:', error);
      setIncidents([]);
      setSelectedIncident(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const profile = await getCurrentUserPermissions();
        const roleNames = profile.roles.map((role) => role.name);
        const canView = roleNames.includes('admin') || roleNames.includes('auditor');
        setAuthorized(canView);
        if (canView) {
          await loadIncidents();
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to load role profile:', error);
        setAuthorized(false);
        setLoading(false);
      }
    };

    bootstrap();
  }, []);

  if (loading && authorized === null) {
    return <div style={{ color: theme.text }}>Loading...</div>;
  }

  if (!authorized) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ ...cardStyle(theme.border, theme.cardBackground), padding: 24, maxWidth: 720 }}>
          <h1 style={{ marginTop: 0, color: theme.text }}>Break-Glass Incidents</h1>
          <p style={{ marginBottom: 0, color: theme.textSecondary }}>
            Access is limited to users with the <strong>admin</strong> or <strong>auditor</strong> role.
          </p>
        </div>
      </div>
    );
  }

  const actorIdentity = identityLabel(
    selectedIncident?.activated_by_name,
    selectedIncident?.activated_by_username,
    selectedIncident?.activated_by_email,
    selectedIncident?.activated_by_user_id,
  );
  const authorIdentity = identityLabel(
    selectedIncident?.created_by_name,
    selectedIncident?.created_by_username,
    selectedIncident?.created_by_email,
    selectedIncident?.created_by_user_id,
  );

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, gap: 16 }}>
        <div>
          <h1 style={{ color: theme.text, fontSize: 28, margin: 0 }}>Break-Glass Incidents</h1>
          <p style={{ color: theme.textSecondary, margin: '8px 0 0 0' }}>
            Cross-pipeline view of emergency activations, incident references, and operator identity.
          </p>
        </div>
        <button
          onClick={() => loadIncidents()}
          style={{
            border: `1px solid ${theme.border}`,
            background: theme.cardBackground,
            color: theme.text,
            borderRadius: 8,
            padding: '10px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontWeight: 600,
          }}
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </div>

      <div style={{ ...cardStyle(theme.border, theme.cardBackground), padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: theme.textSecondary, fontSize: 12, fontWeight: 600 }}>
            Search
            <input
              value={filters.q}
              onChange={(event) => setFilters((current) => ({ ...current, q: event.target.value }))}
              placeholder="Incident ref, pipeline, username, email, reason"
              style={{
                border: `1px solid ${theme.border}`,
                background: theme.background,
                color: theme.text,
                borderRadius: 8,
                padding: '10px 12px',
              }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: theme.textSecondary, fontSize: 12, fontWeight: 600 }}>
            Pipeline ID
            <input
              value={filters.pipelineId}
              onChange={(event) => setFilters((current) => ({ ...current, pipelineId: event.target.value }))}
              placeholder="Optional exact pipeline ID"
              style={{
                border: `1px solid ${theme.border}`,
                background: theme.background,
                color: theme.text,
                borderRadius: 8,
                padding: '10px 12px',
              }}
            />
          </label>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button
            onClick={() => loadIncidents()}
            style={{
              border: 'none',
              background: theme.buttonBackground,
              color: theme.buttonText,
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontWeight: 700,
            }}
          >
            <Search size={16} />
            Apply Filters
          </button>
          <button
            onClick={() => {
              const nextFilters = { q: '', pipelineId: '' };
              setFilters(nextFilters);
              loadIncidents(nextFilters);
            }}
            style={{
              border: `1px solid ${theme.border}`,
              background: theme.cardBackground,
              color: theme.text,
              borderRadius: 8,
              padding: '10px 14px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            Clear
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(480px, 1.15fr) minmax(380px, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={{ ...cardStyle(theme.border, theme.cardBackground), overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: `1px solid ${theme.border}`, color: theme.text, fontWeight: 700 }}>
            Incidents
          </div>
          {loading ? (
            <div style={{ padding: 20, color: theme.text }}>Loading...</div>
          ) : incidents.length === 0 ? (
            <div style={{ padding: 28, color: theme.textSecondary }}>No break-glass incidents matched the current filters.</div>
          ) : (
            <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {incidents.map((incident) => {
                const isSelected = selectedIncident?.version_id === incident.version_id;
                const operator = identityLabel(
                  incident.activated_by_name,
                  incident.activated_by_username,
                  incident.activated_by_email,
                  incident.activated_by_user_id,
                );
                return (
                  <button
                    key={`${incident.version_id}-${incident.activated_at || incident.created_at}`}
                    onClick={() => setSelectedIncident(incident)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      border: 'none',
                      borderBottom: `1px solid ${theme.border}`,
                      background: isSelected ? theme.hoverBackground : 'transparent',
                      padding: 16,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
                      <div style={{ color: theme.text, fontWeight: 700 }}>
                        {incident.pipeline_name || incident.pipeline_id} · v{incident.version}
                      </div>
                      <div style={{ color: theme.textSecondary, fontSize: 12 }}>
                        {new Date(incident.activated_at || incident.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                      <span style={{ padding: '3px 8px', borderRadius: 999, background: theme.warningLight, color: theme.warningDark, fontSize: 12, fontWeight: 700 }}>
                        {incident.details.incident_reference || 'No incident ref'}
                      </span>
                      <span style={{ padding: '3px 8px', borderRadius: 999, background: theme.background, color: theme.text, fontSize: 12, fontWeight: 700 }}>
                        {operator.primary}
                      </span>
                    </div>
                    <div style={{ color: theme.text, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                      {incident.details.reason_summary || 'No reason summary'}
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: 12 }}>
                      {operator.secondary || incident.pipeline_id}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ ...cardStyle(theme.border, theme.cardBackground), overflow: 'hidden' }}>
          <div style={{ padding: 16, borderBottom: `1px solid ${theme.border}`, color: theme.text, fontWeight: 700 }}>
            Incident Details
          </div>
          {!selectedIncident ? (
            <div style={{ padding: 24, color: theme.textSecondary, display: 'flex', alignItems: 'center', gap: 10 }}>
              <AlertTriangle size={18} />
              Select an incident to inspect the emergency activation record.
            </div>
          ) : (
            <div style={{ padding: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12, marginBottom: 16 }}>
                {[
                  ['Pipeline', selectedIncident.pipeline_name || selectedIncident.pipeline_id],
                  ['Version', `v${selectedIncident.version}`],
                  ['Incident Reference', selectedIncident.details.incident_reference || 'Not recorded'],
                  ['Activated At', selectedIncident.activated_at ? new Date(selectedIncident.activated_at).toLocaleString() : 'Unknown'],
                  ['Operator', actorIdentity.primary],
                  ['Operator Contact', actorIdentity.secondary || selectedIncident.activated_by_user_id || 'Unknown'],
                  ['Version Author', authorIdentity.primary],
                  ['Author Contact', authorIdentity.secondary || selectedIncident.created_by_user_id || 'Unknown'],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: theme.background, borderRadius: 8, padding: 12 }}>
                    <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>{label}</div>
                    <div style={{ color: theme.text, fontSize: 13, fontWeight: 600, wordBreak: 'break-word' }}>{value}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'grid', gap: 10 }}>
                {[
                  ['Reason Summary', selectedIncident.details.reason_summary],
                  ['Operational Impact', selectedIncident.details.impact_description],
                  ['Customer / Business Impact', selectedIncident.details.customer_impact],
                  ['Mitigation Plan', selectedIncident.details.mitigation_plan],
                  ['Rollback / Remediation Plan', selectedIncident.details.rollback_plan],
                ].map(([label, value]) => (
                  <div key={label} style={{ background: theme.background, borderRadius: 8, padding: 12 }}>
                    <div style={{ color: theme.textSecondary, fontSize: 12, marginBottom: 4 }}>{label}</div>
                    <div style={{ color: theme.text, fontSize: 13, whiteSpace: 'pre-wrap' }}>{value || 'Not recorded'}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BreakGlassIncidentsPage;
